import clsx from 'clsx';
import {
  type DragEvent,
  type PropsWithChildren,
  type TouchEvent,
  useRef,
  useState,
  type CSSProperties,
  Children,
} from 'react';

import { Menu } from '../../../../navigation/menu/Menu.js';
import { useActionsMenuAlignment } from '../../hooks/useActionsMenuAlignment.js';
import type { DataTableV2AlignmentColumnDef } from '../ColumnAlignment.js';
import { nestedInteractionButtonCSS } from '../nested-interaction-button.sty.js';
import { useCellActions } from './useCellActions.js';
import {
  DataTableV2ColumnContextProvider,
  useDataTableV2,
} from '../../contexts/DataTableV2Context.js';

/**
 * Small wrapper that makes sure to only render the defined actions when the menu
 * is open.
 * @internal
 */
export function UserActionsMenu(
  props: PropsWithChildren<{
    className?: string;
    style?: CSSProperties;
    type: 'cell' | 'header';
    columnId: string;
    rowId: string;
    'aria-label': string;
    'data-header-row'?: number;
    'data-column'?: number;
    virtualizationContainer?: HTMLDivElement;
    alignment?: DataTableV2AlignmentColumnDef['alignment'];
    dragAndDropProps?: {
      role: string;
      tabIndex: number;
      'aria-disabled': boolean;
      'aria-pressed': boolean | undefined;
      'aria-roledescription': string;
      'aria-describedby': string;
      draggable: boolean;
      onDragStart: (event: DragEvent<HTMLElement>) => void;
      onTouchStart: (event: TouchEvent<HTMLElement>) => void;
      onTouchMove: (event: TouchEvent<HTMLElement>) => void;
    };
  }>,
) {
  const {
    type,
    columnId,
    rowId,
    style,
    'aria-label': ariaLabel,
    'data-header-row': dataHeaderRow,
    'data-column': dataColumn,
    virtualizationContainer,
    dragAndDropProps,
    className: propsClassName,
  } = props;
  const [menuOpen, setMenuOpen] = useState(false);

  const triggerProps = {
    type: 'button',
    className: clsx(propsClassName, nestedInteractionButtonCSS),
    style,
    'aria-label': ariaLabel,
    'data-header-row': dataHeaderRow,
    'data-column': dataColumn,
    'data-trigger': 'useraction',
  } as const;

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const table = useDataTableV2();
  const alignment = useActionsMenuAlignment(
    props.alignment,
    buttonRef,
    virtualizationContainer,
  );

  const { builtinActions, customActions } = useCellActions({
    enabled: menuOpen,
    rowId,
    columnId,
    type,
  });

  // Avoid spawning the menu while it is closed to avoid react render
  // performance issues.
  if (!menuOpen) {
    return (
      <button
        {...triggerProps}
        onClick={() => setMenuOpen(true)}
        ref={buttonRef}
        {...dragAndDropProps}
      />
    );
  }

  const column = table.getColumn(columnId);

  return (
    <Menu open={menuOpen} onOpenChange={setMenuOpen}>
      <Menu.Trigger>
        <button {...triggerProps} ref={buttonRef} />
      </Menu.Trigger>
      {menuOpen === true && (
        <DataTableV2ColumnContextProvider value={column}>
          <Menu.Content alignment={alignment}>
            {builtinActions !== null && builtinActions?.length !== 0 ? (
              /*
               * Put the builtin actions on top of the menu
               * and separate them from custom actions by a horizontal line.
               */
              <Menu.Group>
                {
                  /*
                   Run all actions through a Children map so they will receive
                   an automatic key and do not violate react key map rules.
                  */
                  Children.map(builtinActions, (child) => child)
                }
              </Menu.Group>
            ) : null}
            {/*
           Run all actions through a Children map so they will receive
           an automatic key and do not violate react key map rules.
          */}
            {Children.map(customActions, (child) => child)}
          </Menu.Content>
        </DataTableV2ColumnContextProvider>
      )}
    </Menu>
  );
}
